import { ChatCircle, DotsThreeVertical, Heart, Trash } from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Post } from '@sirocco/shared';
import { posts as postsApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/Bits';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { timeAgo } from '../../utils/format';
import { t } from '../../i18n';

/**
 * Couleur d'accent de chaque serveur.
 *
 * Un post ne transporte que l'identifiant de son serveur, jamais son
 * theme : sans cette table, un post new-yorkais s'afficherait en
 * terracotta de Marrakech, la ou la maquette veut la couleur de sa ville.
 */
const SERVER_ACCENTS: Record<string, string> = {
  marrakech: '#B8451B',
  londres: '#C1272D',
  'new-york': '#5A5FA8',
  lisbonne: '#2E8F7A',
};

/**
 * Mes posts, toutes destinations confondues.
 *
 * Chaque carte rappelle le serveur d'origine : c'est le seul endroit de
 * l'application ou des posts de plusieurs villes se croisent, et un post
 * new-yorkais au milieu du feed de Marrakech doit rester lisible.
 */
export function MyPostsScreen() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { data, loading, error, reload, setData } = useAsync(
    () => postsApi.byAuthor(user!.id),
    [user?.id],
    'my-posts',
  );
  const [menuPost, setMenuPost] = useState<Post | null>(null);
  /* Le bouton d'ou le menu sort : un par ligne, donc retenu au toucher. */
  const menuAnchor = useRef<HTMLElement | null>(null);
  const { pending, request, cancel, confirm } = useConfirm();

  /*
    Qui a aime ce post, demande post par post.

    Jamais d'avance : un like ne parait nulle part ailleurs, et charger
    la liste de tous les posts pour la montrer sur un seul irait contre
    ce que ce geste veut dire.
  */
  const [likesPost, setLikesPost] = useState<Post | null>(null);
  const aimants = useAsync(
    () => (likesPost ? postsApi.quiAAime(likesPost.id) : Promise.resolve([])),
    [likesPost?.id],
  );

  async function onDelete(post: Post) {
    setData((previous) => previous.filter((item) => item.id !== post.id));
    try {
      await postsApi.remove(post);
    } catch {
      reload();
    }
  }

  /** Comme dans le feed : le corps du post mene a son fil de commentaires. */
  function openComments(post: Post) {
    navigate(`/app/feed/${post.id}/comments`, { state: { post } });
  }

  return (
    <SubScreen
      title={t.fil.mesPosts.titre}
      action={
        data && (
          <span className="feed__count">
            {t.fil.mesPosts.compte(data.length)}
          </span>
        )
      }
      /*
        Les fenetres passent par la fente prevue : posees dans le
        contenu, elles s'ancreraient a la zone defilante et s'ouvriraient
        sous le pli des qu'on a fait defiler la liste.
      */
      overlay={
        <>
          <Sheet
            open={menuPost !== null}
            anchor={menuAnchor}
            onClose={() => setMenuPost(null)}
          >
            {menuPost && (
              <>
                {/*
                  Le seul endroit de l'application ou l'on voit qui a
                  aime. Pas sur le fil, pas sur le post, pas par une
                  notification : on vient le chercher, chez soi, quand on
                  veut savoir.
                */}
                <SheetAction
                  icon={<Heart size={22} />}
                  onClick={() => {
                    setLikesPost(menuPost);
                    setMenuPost(null);
                  }}
                >
                  {t.fil.mesPosts.voirQuiAAime}
                </SheetAction>
                <SheetAction
                  icon={<Trash size={22} />}
                  tone="danger"
                  onClick={() => {
                    setMenuPost(null);
                    request(t.fil.commun.supprimerPostQ, () => void onDelete(menuPost));
                  }}
                >
                  {t.fil.commun.supprimerPost}
                </SheetAction>
              </>
            )}
          </Sheet>

          <Sheet large open={likesPost !== null} onClose={() => setLikesPost(null)} title={t.fil.mesPosts.jaime}>
            {aimants.loading && <ScreenLoader />}
            {!aimants.loading && aimants.data?.length === 0 && (
              <p className="pr-blocked__intro">{t.fil.mesPosts.personne}</p>
            )}
            {aimants.data?.map((membre) => (
              <div key={membre.id} className="card ms-person">
                <Link to={`/app/members/${membre.id}`} aria-label={membre.username}>
                  <Avatar user={membre} size={44} />
                </Link>
                <div className="ms-person__body">
                  <Link to={`/app/members/${membre.id}`} className="ms-person__name">
                    {membre.username}
                  </Link>
                </div>
              </div>
            ))}
          </Sheet>

          <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />
        </>
      }
    >
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {data && data.length === 0 && (
        <EmptyState title={t.fil.mesPosts.vide} hint={t.fil.mesPosts.videAide} />
      )}

      <div className="feed__saved-list">
        {data?.map((post) => (
          <article key={post.id} className="feed-mine" onClick={() => openComments(post)}>
            <header className="feed-mine__head">
              <span
                className="feed-mine__server"
                style={{ color: SERVER_ACCENTS[post.serverId] ?? 'var(--s-primary)' }}
              >
                {serverLabel(post.serverId)}
              </span>
              <span className="feed-mine__time">· {timeAgo(post.createdAt)}</span>
              <button
                className="feed-mine__menu"
                aria-label={t.fil.commun.actionsPost}
                onClick={(event) => {
                  // Le menu a son propre geste : sans cet arret, il
                  // ouvrirait aussi le fil de commentaires derriere lui.
                  event.stopPropagation();
                  menuAnchor.current = event.currentTarget;
                  setMenuPost(post);
                }}
              >
                <DotsThreeVertical size={20} weight="bold" />
              </button>
            </header>

            <p className="feed-mine__content">{post.content}</p>

            <footer className="feed-mine__stats">
              <span className="feed-mine__stat">
                <ChatCircle size={17} />
                {post.commentCount > 0 && post.commentCount}
              </span>
              <span
                className={
                  post.liked ? 'feed-mine__stat feed-mine__stat--liked' : 'feed-mine__stat'
                }
              >
                <Heart size={17} weight={post.liked ? 'fill' : 'regular'} />
                {post.likeCount > 0 && post.likeCount}
              </span>
            </footer>
          </article>
        ))}
      </div>
    </SubScreen>
  );
}

/**
 * Nom lisible d'un serveur a partir de son identifiant.
 * Les posts portent l'identifiant, pas le libelle : on le remet en forme
 * ici plutot que d'alourdir chaque post d'un objet serveur complet.
 */
function serverLabel(serverId: string): string {
  return serverId
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
